import React, { Fragment } from "react";
import QuotablesGroup from "./QuotablesGroup";
import QuoteItemPicker from "./QuoteItemPicker";
import Button from "../Button";
import SampleInformation from "./SampleInformation";
import { fetchJson } from "../../lib/Fetching";
import _ from "underscore";

type Props = {
  sampleId: ?number,
  clientSuiteId: ?number,
  matrixId: ?number,
  clientSpecificationId: ?number,
  companyId: ?number,
  quote_item_ids: Array<number>,
  onChangeQuoteItems: Function,
};

export default class AdditionalTestingSelector extends React.Component<Props> {
  state = {
    showAdditionalTesting: false,
    selectedQuoteItemIds: [],
    loadedQuotables: false,
    quotables: [],
  };

  static defaultProps = {
    quote_item_ids: [],
  };

  componentDidMount() {
    const { quote_item_ids } = this.props;
    if (quote_item_ids.length === 0) this.setState({ loadedQuotables: true });
    if (this.props.quote_item_ids.length > 0) this.fetchSampleQuoteItems();
  }

  // reload quotables when quote items change
  componentDidUpdate(prevProps) {
    if (!_.isEqual(this.props.quote_item_ids, prevProps.quote_item_ids)) {
      this.fetchSampleQuoteItems();
    }
  }

  fetchSampleQuoteItems = () => {
    const { quote_item_ids } = this.props;
    this.setState({ loadedQuotables: false });
    return fetchJson("/quoting/quotables", {
      quote_item_ids,
    }).then(({ quotables }) => {
      this.setState({
        quotables: quote_item_ids
          .map((id) => quotables.filter((q) => q.quote_item_id === id)[0])
          .filter((quotable) => quotable),
        loadedQuotables: true,
      });
    });
  };

  addQuoteItems = (newQuoteItemIds) => {
    const { onChangeQuoteItems, quote_item_ids } = this.props;
    onChangeQuoteItems(quote_item_ids.concat(newQuoteItemIds));
  };

  removeQuoteItems = () => {
    const { onChangeQuoteItems, quote_item_ids } = this.props;
    const { selectedQuoteItemIds } = this.state;

    onChangeQuoteItems(quote_item_ids.filter((id) => !selectedQuoteItemIds.includes(id)));
    this.setState({ selectedQuoteItemIds: [] });
  };

  // Only lab samples that use client suites and not direct registrations
  isUnregisteredWithClientSuite = () => {
    const { sampleId, clientSuiteId } = this.props;
    return !sampleId && Boolean(clientSuiteId);
  };

  render() {
    const {
      sampleId,
      clientSuiteId,
      clientSpecificationId,
      quote_item_ids,
      matrixId,
      companyId,
    } = this.props;
    const {
      quotables,
      loadedQuotables,
      showAdditionalTesting,
      selectedQuoteItemIds,
    } = this.state;

    if (!loadedQuotables && quote_item_ids.length === 0)
      return (
        <div className="sample_testing">
          <div
            className="basic-panel with-padding with-rounded-corners with-shadow"
            style={{ margin: 10, marginTop: 0 }}
          >
            <div>Loading...</div>
          </div>
        </div>
      );

    const excludeQuoteItemIds = this.isUnregisteredWithClientSuite()
        ? quotables.map((q) => q.quote_item_id)
        : [];

    return (
      <div className="sample_testing">
        <div
          className="basic-panel with-padding with-rounded-corners with-shadow"
          style={{ margin: 10, marginTop: 0, marginLeft: 0, marginRight: 0 }}
        >
          {sampleId && <SampleInformation sampleId={sampleId} type="adding_testing" />}
          {quotables.length > 0 && (
            <Fragment>
              <h4>Tests To be Added</h4>
              <div className="pull-left">
                <Button
                  title="Remove Selected"
                  deleting
                  small
                  disabled={selectedQuoteItemIds.length == 0}
                  onClick={this.removeQuoteItems}
                />
              </div>
              <QuotablesGroup
                selectQuoteItems
                quotables={quotables}
                selectedIds={selectedQuoteItemIds}
                onChangeIds={(selectedQuoteItemIds) => this.setState({ selectedQuoteItemIds })}
              />
              <p className="text-right small" style={{ marginTop: 10 }}>
                Individual item prices shown are the standard prices from the quote and do not
                include turnaround premiums that may apply to this sample.
              </p>
              {loadedQuotables &&
                _.uniq(quotables.map((q) => q.quote_item_id)).length !==
                  quote_item_ids.length && (
                  <p className="alert alert-warning">
                    Warning: You have selected the same testing more than once!
                  </p>
                )}
            </Fragment>
          )}
          <Button
            title="Add More Tests"
            icon="fa-plus"
            style={{ marginTop: quotables.length == 0 ? 0 : 10 }}
            onClick={() => this.setState({ showAdditionalTesting: !showAdditionalTesting })}
          />
        </div>
        {showAdditionalTesting && (
          <QuoteItemPicker
            sampleId={sampleId}
            clientSuiteId={clientSuiteId}
            clientSpecificationId={clientSpecificationId}
            matrixId={matrixId}
            companyId={companyId}
            excludeQuoteItemIds={excludeQuoteItemIds}
            onAddQuoteItemIds={this.addQuoteItems}
            onCancel={() => this.setState({ showAdditionalTesting: false })}
          />
        )}
      </div>
    );
  }
}
